import { useResizeObserver } from '@mantine/hooks'
import { snakeCase } from 'lodash-es'
import { type HTMLAttributes, useMemo, useState } from 'react'
import { match, P } from 'ts-pattern'
import type { User } from 'src/services/apollo/generated/graphql'
import { cn } from 'src/utils/cx'
import { hash } from 'src/utils/hash'
import { getImageThumbnailUrl } from 'src/utils/media'
import { getUserDisplayName } from 'src/utils/user'

export type UserAvatarImageProps = HTMLAttributes<HTMLElement> & {
  user: Pick<
    User,
    | 'id'
    | 'avatarMediaId'
    | 'avatarSmallThumbnailMediaUrl'
    | 'displayName'
    | 'username'
  >
  /**
   * 'thumbnail': avatarSmallThumbnailMediaUrl for 1x, thumbnail for 2x
   * 'avatarSmallThumbnail': always avatarSmallThumbnailMediaUrl
   *
   * @default 'avatarSmallThumbnail'
   */
  quality?: 'thumbnail' | 'avatarSmallThumbnail'
}

function getAvatarSrcAndSrcSet({
  quality,
  user,
}: Pick<UserAvatarImageProps, 'quality' | 'user'>) {
  const { avatarMediaId, avatarSmallThumbnailMediaUrl } = user

  const thumbnailUrl = getImageThumbnailUrl(avatarMediaId)

  let src = avatarSmallThumbnailMediaUrl
  let srcSet = ''
  // Returns thumbnailUrl directly if avatarSmallThumbnail is not available
  if (!avatarSmallThumbnailMediaUrl) {
    src = thumbnailUrl
  } else if (quality === 'thumbnail') {
    // Keeping src; Update srcSet only
    srcSet = `${avatarSmallThumbnailMediaUrl}, ${thumbnailUrl} 2x`
  }

  return {
    src,
    srcSet: srcSet || undefined,
  }
}

export default function UserAvatarImage({
  user,
  className,
  style,
  quality,
  ...props
}: UserAvatarImageProps) {
  const { src, srcSet } = getAvatarSrcAndSrcSet({ quality, user })

  const [failed, setFailed] = useState(false)
  const userName = getUserDisplayName(user)

  return (
    <div
      className={cn('aspect-square overflow-hidden rounded-full', className)}
      style={style}
      {...props}
    >
      {!src || failed ? (
        <ImagePlaceholder user={user} />
      ) : (
        <img
          src={src}
          srcSet={srcSet || undefined}
          // TODO: I18N?
          alt={`Avatar image of ${userName}`}
          crossOrigin="anonymous"
          onError={() => {
            setFailed(true)
          }}
          className="size-full object-cover object-center"
        />
      )}
    </div>
  )
}

// The factor to compute the font size of username initials.
// font size = avatar width / factor
const INITIALS_FONT_SIZE_FACTOR = 2.5

const PLACEHOLDER_COLORS = [
  'from-red-200 to-red-400 text-white',
  'from-green-400 to-blue-400 text-white',
  'from-yellow-500 to-red-800 text-white',
  'from-yellow-200 to-amber-400 text-amber-900',
  'from-pink-100 to-violet-300 text-violet-900',
  'from-red-400 to-amber-500 text-white',
  'from-yellow-300 to-green-300 text-green-900',
  'from-pink-300 to-red-500 text-white',
  'from-sky-300 to-yellow-300 text-sky-900',
  'from-indigo-100 to-blue-300 text-indigo-900',
]

function ImagePlaceholder({
  user,
}: Required<Pick<UserAvatarImageProps, 'user'>>) {
  const placeholderSeed = user.id ?? user.username ?? user.displayName ?? ''
  const color = useMemo(
    () => PLACEHOLDER_COLORS[hash(placeholderSeed) % PLACEHOLDER_COLORS.length],
    [placeholderSeed],
  )

  const initials = useMemo(() => {
    const name = getUserDisplayName(user)
    // `user-{userId}` is the default username (auto-created during sign-up).
    // Most times it is `user-1xxx`, resulting in the initials to always be "U1".
    // It is better to only display colors in such cases.
    if (!name || name === `user-${user.id}`) return
    if (name.length <= 2) return name
    return snakeCase(name)
      .split('_')
      .slice(0, 2)
      .map(i => i[0])
      .join('')
  }, [user])

  const [ref, rect] = useResizeObserver()

  return (
    <div
      ref={ref}
      className={cn(
        'size-full grid place-content-center cursor-default select-none bg-gradient-to-br',
        color,
      )}
    >
      <span
        className="font-inter font-semibold uppercase"
        style={{ fontSize: rect.width / INITIALS_FONT_SIZE_FACTOR }}
      >
        {initials}
      </span>
    </div>
  )
}
